import { useEffect, useMemo, useState } from "react";
import { Loader2, Wallet, PiggyBank, Download, Scissors, CalendarDays, ChevronLeft, ChevronRight } from "lucide-react";
import { api } from "@/lib/api";
import { rp, BULAN } from "@/lib/payroll";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/i18n";
import { EmployeeAvatar } from "@/components/EmployeeAvatar";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const Section = ({ title, icon: Icon, children, accent = "#0071E3" }) => (
  <section className="liquid-glass-card p-5">
    <div className="mb-4 flex items-center gap-2">
      <Icon className="h-4 w-4" style={{ color: accent }} />
      <h2 className="text-[15px] font-semibold tracking-tight sm:text-[16px]">{title}</h2>
    </div>
    {children}
  </section>
);

export default function MyData() {
  const { user } = useAuth();
  const { t } = useT();
  const now = new Date();
  const [emp, setEmp] = useState(null);
  const [kasbon, setKasbon] = useState(null);
  const [savings, setSavings] = useState(null);
  const [slips, setSlips] = useState([]);
  const [potongan, setPotongan] = useState([]);
  const [kasbonHist, setKasbonHist] = useState([]);
  const [att, setAtt] = useState(null);
  const [bulan, setBulan] = useState(now.getMonth() + 1);
  const [tahun, setTahun] = useState(now.getFullYear());
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    Promise.all([
      api.get("/me/employee"),
      api.get("/me/kasbon").catch(() => ({ data: null })),
      api.get("/me/savings").catch(() => ({ data: null })),
      api.get("/me/salary-history").catch(() => ({ data: { periods: [] } })),
      api.get("/me/potongan-history").catch(() => ({ data: { records: [] } })),
      api.get("/me/kasbon-history").catch(() => ({ data: { records: [] } })),
    ])
      .then(([e, k, s, sl, pot, kh]) => {
        if (!alive) return;
        setEmp(e.data); setKasbon(k.data); setSavings(s.data);
        setSlips(sl.data?.periods || []);
        setPotongan(pot.data?.records || []);
        setKasbonHist(kh.data?.records || []);
      })
      .catch(() => alive && setErr(true))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    let alive = true;
    api.get("/me/attendance/month", { params: { bulan, tahun } })
      .then((r) => alive && setAtt(r.data))
      .catch(() => alive && setAtt(null));
    return () => { alive = false; };
  }, [bulan, tahun]);

  const shiftMonth = (n) => {
    let b = bulan + n, y = tahun;
    if (b < 1) { b = 12; y -= 1; } else if (b > 12) { b = 1; y += 1; }
    setBulan(b); setTahun(y);
  };

  const attTiles = useMemo(() => {
    const s = att?.summary || {};
    return [
      { label: t("att.full"), v: s.full ?? 0, color: "#0E8A46", bg: "#E9F9EF" },
      { label: t("att.half"), v: s.half ?? 0, color: "#B4740A", bg: "#FFF4E0" },
      { label: t("att.absent"), v: s.zero ?? 0, color: "#C0362C", bg: "#FDECEA" },
      { label: t("att.needs"), v: s.needs_review ?? 0, color: "#6E6E73", bg: "#F2F2F7" },
    ];
  }, [att, t]);

  if (loading) return <div className="grid place-items-center py-24"><Loader2 className="h-6 w-6 animate-spin text-[#B0B0B8]" /></div>;

  if (err || !emp) {
    return (
      <div data-testid="my-data-not-linked" className="mx-auto max-w-lg rounded-2xl border border-[#FDE8C4] bg-[#FFF8EC] p-6 text-center text-[14px] text-[#8A5A00]">
        {t("me.notLinked")}
      </div>
    );
  }

  return (
    <div data-testid="my-data-page" className="w-full space-y-5">
      {/* Identity header */}
      <div className="flex items-center gap-4 rounded-2xl border border-white/60 bg-white/70 p-5 backdrop-blur-xl">
        <EmployeeAvatar id={emp.id} nama={emp.nama} hasPhoto={emp.has_photo} size={56} />
        <div className="min-w-0">
          <h1 className="truncate text-[20px] font-semibold tracking-tight sm:text-[24px]">{emp.nama}</h1>
          <p className="text-[13px] text-[#8E8E93]">{[emp.jabatan, emp.emp_code].filter(Boolean).join(" · ") || "—"}</p>
        </div>
      </div>

      {/* Balances */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div data-testid="my-kasbon-card" className="rounded-2xl border border-[#F3C9C4]/60 bg-gradient-to-br from-[#FDECEA] to-white p-5">
          <div className="flex items-center gap-2 text-[#C0362C]"><Wallet className="h-4 w-4" /><span className="text-[12.5px] font-semibold">{t("me.kasbonLeft")}</span></div>
          <p className="num mt-1.5 text-[24px] font-bold text-[#C0362C]">Rp {rp(kasbon?.balance || 0)}</p>
        </div>
        <div data-testid="my-savings-card" className="rounded-2xl border border-[#BFE3C8]/60 bg-gradient-to-br from-[#EAF8EF] to-white p-5">
          <div className="flex items-center gap-2 text-[#0E8A46]"><PiggyBank className="h-4 w-4" /><span className="text-[12.5px] font-semibold">{t("me.savings")}</span></div>
          <p className="num mt-1.5 text-[24px] font-bold text-[#0E8A46]">Rp {rp(savings?.balance || 0)}</p>
        </div>
      </div>

      {/* Payslips */}
      <Section title={t("me.payslips")} icon={Download}>
        {slips.length === 0 ? (
          <p className="py-6 text-center text-[13px] text-[#8E8E93]">{t("me.noData")}</p>
        ) : (
          <div className="space-y-2">
            {slips.map((p) => (
              <div key={p.period_id} data-testid={`my-slip-${p.period_id}`} className="flex items-center justify-between gap-3 rounded-xl border border-black/[0.05] bg-white/70 px-3.5 py-2.5">
                <div className="min-w-0">
                  <p className="flex items-center gap-1.5 text-[14px] font-semibold text-[#1D1D1F]">{p.is_jarit && <Scissors className="h-3.5 w-3.5 text-[#AF52DE]" />}{p.label}</p>
                  <p className="num text-[12px] text-[#8E8E93]">{t("me.total")}: Rp {rp(p.total)}</p>
                </div>
                <a
                  href={`${BACKEND}/api/me/slip/${p.period_id}`}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`my-slip-download-${p.period_id}`}
                  className="flex h-9 shrink-0 items-center gap-1.5 rounded-full bg-[#0071E3] px-4 text-[13px] font-medium text-white transition-all hover:bg-[#0077ED] active:scale-[0.97]"
                >
                  <Download className="h-3.5 w-3.5" /> {t("me.download")}
                </a>
              </div>
            ))}
          </div>
        )}
      </Section>

      {/* Attendance */}
      <Section title={t("me.attendance")} icon={CalendarDays} accent="#FF9500">
        <div className="mb-4 flex items-center gap-2">
          <button data-testid="my-att-prev" onClick={() => shiftMonth(-1)} className="grid h-9 w-9 place-items-center rounded-full text-[#3A3A3C] transition-colors hover:bg-black/[0.05]"><ChevronLeft className="h-5 w-5" /></button>
          <span className="min-w-[140px] text-center text-[14px] font-semibold">{att?.label || `${BULAN[bulan]} ${tahun}`}</span>
          <button data-testid="my-att-next" onClick={() => shiftMonth(1)} className="grid h-9 w-9 place-items-center rounded-full text-[#3A3A3C] transition-colors hover:bg-black/[0.05]"><ChevronRight className="h-5 w-5" /></button>
        </div>
        <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-4">
          {attTiles.map((x) => (
            <div key={x.label} className="rounded-2xl border border-black/[0.05] bg-white/60 px-3.5 py-3">
              <p className="num text-[22px] font-bold" style={{ color: x.color }}>{x.v}</p>
              <p className="text-[12px] font-medium text-[#6E6E73]">{x.label}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* Deduction history */}
      <Section title={t("me.potonganHistory")} icon={Wallet} accent="#AF52DE">
        {potongan.length === 0 ? (
          <p className="py-6 text-center text-[13px] text-[#8E8E93]">{t("me.noData")}</p>
        ) : (
          <div className="space-y-2">
            {potongan.map((r) => (
              <div key={`${r.bulan}-${r.tahun}`} className="rounded-xl border border-black/[0.05] bg-white/70 px-3.5 py-2.5">
                <div className="flex items-center justify-between">
                  <span className="text-[13.5px] font-semibold">{r.label}</span>
                  <span className="num text-[13px] font-semibold text-[#C0362C]">Rp {rp(r.total)}</span>
                </div>
                <div className="mt-1 space-y-0.5">
                  {r.items.map((it, i) => (
                    <p key={i} className="flex justify-between text-[12px] text-[#8E8E93]"><span className="truncate">{it.keterangan}</span><span className="num">Rp {rp(it.nominal)}</span></p>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </Section>

      {/* Cash advance history */}
      <Section title={t("me.kasbonHistory")} icon={Wallet} accent="#FF3B30">
        {kasbonHist.length === 0 ? (
          <p className="py-6 text-center text-[13px] text-[#8E8E93]">{t("me.noData")}</p>
        ) : (
          <div className="space-y-2">
            {kasbonHist.map((r) => (
              <div key={`${r.bulan}-${r.tahun}`} className="flex items-center justify-between rounded-xl border border-black/[0.05] bg-white/70 px-3.5 py-2.5">
                <div><span className="text-[13.5px] font-semibold">{r.label}</span><span className="ml-2 text-[11.5px] text-[#8E8E93]">{r.source}</span></div>
                <span className="num text-[13px] font-semibold text-[#C0362C]">Rp {rp(r.amount)}</span>
              </div>
            ))}
          </div>
        )}
      </Section>
    </div>
  );
}
